import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import clsx from 'clsx'
import type { ISODate, Task } from '@app/shared'

import { anchorAtPoint, anchorOfElement } from '../../lib/popover'
import { taskFileAway } from '../../lib/motion'
import { dayGroupLabel, dueTone, duePill, UNFILED, type DoneDay } from './derive'
import { More, RowBell, Tick } from './icons'
import { DayEyebrow, DuePill, ListChip, RecurrenceChip, RowRule } from './parts'
import { useTaskMenu } from './TaskEditMenu'

/**
 * ONE TASK ROW — the only row shape on this screen. LifeOS Tasks.dc.html draws exactly one, in
 * three paddings (12px overdue, 11px today, 10px upcoming); everything else about it is the
 * same. A second row shape is how a task list becomes a dashboard.
 *
 * COMPLETION IS THE WHOLE INTERACTION AND IT IS DELIBERATELY BORING. Ticking the box moves the
 * row to the Done log and nothing else happens: no progress ring, no streak, no percentage, no
 * celebration (README §7, "Deliberately absent"). The reward is the Done log's count going up.
 *
 * 13b · WHAT THE MOTION BRIEF ADDED, and why it is not a celebration. The row used to vanish on
 * the next render. It now spends ~900ms FILING ITSELF: box fills green → checkmark pops →
 * strikethrough wipes → label dims → the row travels 26px RIGHT (toward the Done log, which is
 * the column on the right) and collapses. That is not decoration; it answers "did it work" and
 * "where did it go", which are §1's two jobs. There is still no burst, no confetti and no
 * checkmark flourish beyond the pop the brief specifies — and the count roll in the Done log is
 * the reward, exactly as the handoff insists.
 *
 * THE WRITE HAPPENS AT THE END, not the start. `onToggle` fires when the row has finished
 * collapsing, so the row React removes is a row that is already 0px tall — which is why the list
 * closes up instead of snapping. Under `prefers-reduced-motion` the sequencer commits every end
 * state immediately and `onToggle` fires on the same tick: the feedback is gone, the completion
 * is not.
 */
export function TaskRow({
  task,
  today,
  density = 'normal',
  onToggle,
  disabledReason,
}: {
  task: Task
  today: ISODate
  density?: 'overdue' | 'normal' | 'compact'
  onToggle: (v: { id: string; done: boolean }) => void
  /**
   * Set when completion cannot be written. The box then renders as designed but says why —
   * a checkbox that silently fails to tick is the worst possible outcome on this screen.
   */
  disabledReason?: string
}) {
  const tone = dueTone(task, today)
  const pill = duePill(task, today)
  const blocked = disabledReason !== undefined
  // Null when this row is rendered outside a `TaskMenuHost` — the row still works, it just has
  // no menu. That is what keeps TaskRow usable from anywhere without a provider.
  const menu = useTaskMenu()

  const [filing, setFiling] = useState(false)
  const row = useRef<HTMLDivElement>(null)
  const check = useRef<SVGSVGElement>(null)
  const strike = useRef<HTMLSpanElement>(null)

  // The sequence runs in an effect rather than in the click handler because the checkmark and the
  // strikethrough do not exist in the DOM until `filing` has rendered them — starting the
  // animation from the handler would animate two nulls and file the row away silently.
  useEffect(() => {
    if (!filing) return
    const node = row.current
    if (!node) {
      onToggle({ id: task.id, done: true })
      return
    }
    const box = node.getBoundingClientRect()
    const style = getComputedStyle(node)
    // Measured, not assumed: this screen renders the row at three densities, and the group cards
    // separate rows with a sibling rule rather than a border — so a hard-coded 39px/1px would
    // collapse the wrong amount and ADD a border that was never there.
    const run = taskFileAway({
      row: node,
      check: check.current,
      strike: strike.current,
      rowHeight: box.height,
      padTop: parseFloat(style.paddingTop) || 0,
      padBottom: parseFloat(style.paddingBottom) || 0,
      borderTop: parseFloat(style.borderTopWidth) || 0,
      borderBottom: parseFloat(style.borderBottomWidth) || 0,
    })
    let abandoned = false
    void run.finished.then(() => {
      if (!abandoned) onToggle({ id: task.id, done: true })
    })
    return () => {
      abandoned = true
      run.cancel()
    }
    // `onToggle` is a react-query mutate identity that changes on every mutation state change;
    // including it would restart the sequence mid-flight the first time anything else completes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filing, task.id])

  return (
    <div
      ref={row}
      data-filing={filing || undefined}
      // RIGHT-CLICK OPENS THE SAME MENU THE `...` BUTTON DOES, at the pointer — a cursor is a
      // zero-sized anchor rect (`anchorAtPoint`), which is the only difference between the two
      // paths. The native menu is suppressed HERE, on the one element that has a replacement, and
      // nowhere else: a right-click on a link or a text field elsewhere in the app still gets the
      // browser's own. With no menu host above this row (`menu` is null) nothing is suppressed at
      // all, because then there is nothing to put in its place.
      onContextMenu={
        menu && menu.disabledReason === undefined
          ? (e) => {
              e.preventDefault()
              menu.open(task, anchorAtPoint(e.clientX, e.clientY))
            }
          : undefined
      }
      style={
        {
          // HOVER SAYS WHERE IT CAME FROM (motion-brief §2). A task row is a radius-≤10 surface,
          // so its mechanic is the straight inset rule — in the hue of what is true about it:
          // overdue rows answer in the danger red they already wear, everything else in Tasks
          // orange. The faint fill is derived from the same value, so the two can never disagree.
          '--mo-rule': tone === 'overdue' ? 'var(--color-danger-dot)' : 'var(--color-tasks)',
        } as CSSProperties
      }
      className={clsx(
        'group mo-rule flex items-center gap-3',
        // Only while filing: the collapse animates `height`, and without this the row's contents
        // spill out of a box that is closing around them.
        filing && 'overflow-hidden',
        density === 'overdue' && 'px-4 py-3',
        density === 'normal' && 'px-4 py-[11px]',
        density === 'compact' && 'px-4 py-[10px]',
      )}
    >
      <button
        type="button"
        aria-label={`Complete ${task.title}`}
        aria-disabled={blocked || undefined}
        title={disabledReason}
        onClick={(e) => {
          if (blocked || filing) {
            e.preventDefault()
            return
          }
          setFiling(true)
        }}
        className={clsx(
          'mo-file-box mo-press flex size-[18px] flex-none items-center justify-center rounded-[5px] border-[1.7px] bg-surface',
          blocked ? 'cursor-not-allowed' : 'hover:border-ink',
          tone === 'overdue' ? 'border-danger-line-3' : 'border-dash',
        )}
      >
        {/* Present only while filing. A checkmark that exists at opacity 0 on 40 idle rows is 40
            elements a screen reader has to be told to ignore, for a glyph nobody has earned yet. */}
        {filing && (
          <svg
            ref={check}
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-surface opacity-0"
            aria-hidden
          >
            <path d="M5 12.6l4 4L19 7.2" />
          </svg>
        )}
      </button>

      {/* WRAPS, never truncates. The mockup sets `flex:1;min-width:0` and no overflow rule, and
          a task title with its end cut off is the one string on this screen you cannot act on
          without opening something. A long title takes a second line and the row grows. */}
      <span className="relative min-w-0 flex-1">
        <span className="mo-file-label block text-[13.5px] font-medium leading-[1.3] text-ink text-pretty">
          {task.title}
        </span>
        {/* The strikethrough is a drawn rule that WIPES, not a `line-through` that appears. The
            wipe is the direction of reading; the property flipping on is a state you missed. */}
        {filing && (
          <span
            ref={strike}
            aria-hidden
            className="pointer-events-none absolute left-0 top-1/2 h-[1.2px] w-0 bg-microcopy"
          />
        )}
      </span>

      <span className="flex flex-none items-center gap-[7px]">
        {/* PROVENANCE, the way the Calendar draws FROM CHAT — quieter, because a reminder row is
            half the height of a block and a stamp in the block's type size would shout. It is
            read off `proposalId`, the link back to the suggestion the owner accepted; a task he
            typed in carries nothing here, which is what makes the label mean something. */}
        {task.proposalId !== null && (
          <span className="text-[9px] font-semibold leading-none tracking-[.05em] text-microcopy">
            from chat
          </span>
        )}
        {task.reminderAt !== null && (
          <>
            <RowBell size={14} className="text-microcopy" />
            <span className="sr-only">has a reminder</span>
          </>
        )}
        <RecurrenceChip rrule={task.rrule} />
        {pill && <DuePill text={pill} tone={tone} />}
        {/* An unfiled task gets NO list chip. Printing "Inbox" would name a list it was never
            filed into — the Inbox is the absence of one, and the strip below the board says so. */}
        {task.list !== UNFILED && <ListChip list={task.list} />}

        {/* THE ROW MENU'S TRIGGER. Always drawn, never hover-only — the owner's report was that
            there was no way at all to set a date or a list, and a control that appears only under
            a pointer is a control a touch screen, a keyboard and a first-time look all miss.
            SECOND REPORT (2026-08-28): "kind of hard to see — maybe a little square button around
            them, or make them solid or darker." It reads as a button at rest now — a square hit
            target the size of the checkbox it sits across from, a hairline border the same token
            every other quiet-but-visible tile in this app uses (WorkerCard's keycap, the chat
            composer's model chip), `text-muted` rather than the almost-invisible `faintest-2` the
            glyph used to be — and darkens further on hover/open, the row furniture's own
            transition tokens. `:focus-visible` gets its ring for free from the app-wide rule in
            index.css; nothing here overrides it.

            RIGHT-CLICKING THE ROW OPENS THE SAME PANEL. That is a shortcut, not the way in — a
            menu reachable only by right-click is one a phone cannot reach at all, which is
            exactly what the two owner reports above were about. */}
        {menu && (
          <button
            type="button"
            aria-label={`Edit ${task.title}`}
            aria-haspopup="dialog"
            aria-expanded={menu.openId === task.id}
            aria-disabled={menu.disabledReason !== undefined || undefined}
            title={menu.disabledReason ?? 'Due date, list, delete'}
            onClick={(e) => {
              if (menu.disabledReason !== undefined) {
                e.preventDefault()
                return
              }
              if (menu.openId === task.id) {
                menu.close()
                return
              }
              menu.open(task, anchorOfElement(e.currentTarget))
            }}
            className={clsx(
              'flex size-[22px] flex-none items-center justify-center rounded-tile-sm border bg-surface text-muted',
              'transition-colors duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
              'hover:border-line-2 hover:bg-fill-2 hover:text-ink-2',
              menu.openId === task.id ? 'border-line-2 bg-fill-2 text-ink-2' : 'border-line',
            )}
          >
            <More size={14} />
          </button>
        )}
      </span>
    </div>
  )
}

/**
 * A GROUP CARD — a white card, a header pairing a 600 12.5px label with a 400 11px microcopy
 * count ("Today" / "5 items", README §7), then its rows separated by hairlines.
 */
export function TaskCard({
  label,
  qualifier,
  children,
  tone = 'plain',
  header,
}: {
  label?: string
  qualifier?: string
  children: ReactNode
  tone?: 'plain' | 'danger'
  /** Replaces the plain label/qualifier header entirely — the Overdue card brings its own. */
  header?: ReactNode
}) {
  return (
    <section
      className={clsx(
        'flex-none overflow-hidden rounded-card border bg-surface shadow-card',
        tone === 'danger' ? 'border-danger-line' : 'border-line',
      )}
    >
      {header ?? (
        <header className="flex items-baseline gap-[9px] px-4 pb-[9px] pt-3">
          <h2 className="text-[12.5px] font-semibold leading-none">{label}</h2>
          {qualifier && (
            <span className="text-[11px] font-normal leading-none text-microcopy">{qualifier}</span>
          )}
        </header>
      )}
      {children}
    </section>
  )
}

/** A card's rows, hairline-separated, with no grouping. */
export function FlatRows({
  tasks,
  today,
  density = 'normal',
  onToggle,
  disabledReason,
}: {
  tasks: Task[]
  today: ISODate
  density?: 'overdue' | 'normal' | 'compact'
  onToggle: (v: { id: string; done: boolean }) => void
  disabledReason?: string
}) {
  return (
    <>
      {tasks.map((task, i) => (
        <Fragment key={task.id}>
          {i > 0 && <RowRule />}
          <TaskRow
            task={task}
            today={today}
            density={density}
            onToggle={onToggle}
            disabledReason={disabledReason}
          />
        </Fragment>
      ))}
    </>
  )
}

/**
 * A card's rows grouped by their due day, each under a `TOMORROW · FRI 21` eyebrow.
 *
 * The grouping is by DATE, so a day with two things due gets one header and two rows — the
 * mockup never repeats a day header, and a repeated one would read as two separate days.
 */
export function DayGroupedRows({
  tasks,
  today,
  onToggle,
  disabledReason,
}: {
  tasks: Task[]
  today: ISODate
  onToggle: (v: { id: string; done: boolean }) => void
  disabledReason?: string
}) {
  const days: Array<{ date: ISODate; tasks: Task[] }> = []
  for (const task of tasks) {
    const date = task.due as ISODate
    const last = days[days.length - 1]
    if (last && last.date === date) last.tasks.push(task)
    else days.push({ date, tasks: [task] })
  }

  return (
    <>
      {days.map((day, i) => (
        <Fragment key={day.date}>
          {i > 0 && <RowRule />}
          <div className={clsx('px-4 pb-1', i > 0 ? 'pt-[9px]' : 'pt-0')}>
            <DayEyebrow>{dayGroupLabel(day.date, today)}</DayEyebrow>
          </div>
          {day.tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              today={today}
              density="compact"
              onToggle={onToggle}
              disabledReason={disabledReason}
            />
          ))}
        </Fragment>
      ))}
      <div className="pb-[5px]" />
    </>
  )
}

/**
 * THE DONE LOG'S OWN LENS, IN THE CENTER PANE (owner report, 2026-08-28: selecting "Done log" on
 * the left showed only an explainer card here — the actual completions lived exclusively in the
 * right rail, out of reach of anyone scanning the middle column the other four lenses use).
 *
 * `days` is `doneDays()`'s own output — local-day grouped, newest day first, the exact function
 * components/tasks/DoneLog.tsx groups the rail with — so the two can never disagree about which
 * day something finished on. This is not a second copy of that grouping logic, just a second
 * rendering of it, in the center pane's own list idiom: a hairline-separated card, day eyebrows,
 * struck-through titles — the same furniture `DayGroupedRows` above draws for open work.
 */
export function DoneRows({ days }: { days: DoneDay[] }) {
  return (
    <>
      {days.map((day, i) => (
        <Fragment key={day.date}>
          {i > 0 && <RowRule />}
          <div className={clsx('px-4 pb-1', i > 0 ? 'pt-[9px]' : 'pt-0')}>
            <DayEyebrow>{day.label}</DayEyebrow>
          </div>
          {day.tasks.map((task, j) => (
            <Fragment key={task.id}>
              {j > 0 && <RowRule />}
              <DoneCenterRow task={task} />
            </Fragment>
          ))}
        </Fragment>
      ))}
    </>
  )
}

/** A completed row, center-pane sized — components/tasks/DoneLog.tsx's `DoneRow` at row width. */
function DoneCenterRow({ task }: { task: Task }) {
  return (
    <div className="flex items-center gap-3 px-4 py-[10px]">
      <Tick size={14} strokeWidth={2.2} className="flex-none text-live-dot" />
      <span className="min-w-0 flex-1 text-[13.5px] leading-[1.3] text-muted line-through text-pretty">
        {task.title}
      </span>
      <RecurrenceChip rrule={task.rrule} />
    </div>
  )
}
